import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CancelSubscriptionModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');

  const reasons = [
    { value: 'zu_teuer', label: 'Zu teuer' },
    { value: 'fehlende_features', label: 'Fehlende Features' },
    { value: 'pension', label: 'Pension' },
    { value: 'sonstiges', label: 'Sonstiges' }
  ];

  const handleCancel = async () => {
    if (!reason) {
      toast.error('Bitte wählen Sie einen Grund aus');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/user/cancel-subscription`,
        {
          reason,
          feedback: feedback || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Abo erfolgreich gekündigt');
      
      if (response.data.active_until) {
        toast.info(`Ihr Zugang bleibt aktiv bis: ${new Date(response.data.active_until).toLocaleDateString('de-AT')}`);
      }

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Kündigen des Abos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <DialogTitle>Abo kündigen</DialogTitle>
          </div>
          <DialogDescription>
            Möchten Sie Ihr AdSimplify-Abo wirklich kündigen? Ihr Zugang bleibt bis zum Ende der aktuellen Abrechnungsperiode aktiv.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Grund für die Kündigung</Label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Bitte wählen...</option>
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {(reason === 'fehlende_features' || reason === 'sonstiges') && (
            <div className="space-y-2">
              <Label>Ihr Feedback (optional)</Label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={
                  reason === 'fehlende_features'
                    ? 'Welche Features fehlen Ihnen?'
                    : 'Teilen Sie uns mit, warum Sie kündigen...'
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleCancel}
            disabled={loading || !reason}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {loading ? 'Kündigen...' : 'Abo kündigen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelSubscriptionModal;
